/**
 * Aggregation. Pure functions over already-loaded rows — no database, no I/O,
 * so the same code runs on the server and in the client's results screen.
 *
 * The plan's hardest rule is enforced here structurally rather than by
 * discipline: **descriptive aspects are never averaged into an overall score.**
 * Three things make that hard to violate by accident.
 *
 *   1. **Descriptive** aspects are never averaged into anything, by anything
 *      here. `aspectProfile` and `topicValue` both take the aspect's `kind` and
 *      refuse; `overallValue` skips a descriptive topic outright rather than
 *      folding it in. A mean over a Luck↔Strategy axis is a number describing no
 *      game anybody played, and it stays impossible to produce by forgetting.
 *
 *      This rule used to be wider — *no* function took a set of aspects and
 *      returned one number, so that a composite score could not exist at all.
 *      `overallValue` ends that, deliberately and by request: the night now has
 *      an overall, and it is the mean of the evaluative topics one seat gave.
 *      What replaced the blanket ban is the sentence above plus the fact that the
 *      derived figure is never stored (`OverallRating` holds only the override),
 *      so it cannot drift away from the answers it is made of. The headline is
 *      untouched and still nobody's average: it is `headline_rating`, typed in
 *      directly, computed by `sessionHeadline` / `gameHeadline`, neither of which
 *      can see an aspect value. The two sit side by side on purpose.
 *   2. Every aggregate carries its own `n`, `spread` and `split`. A caller that
 *      wants a bare mean has to walk past the evidence that the group disagreed.
 *   3. The refusal is in the return type, not in a convention. `aspectProfile`
 *      hands back a null `mean` and `median` for a DESCRIPTIVE aspect, so a
 *      renderer cannot print one by forgetting the rule — there is no descriptive
 *      average anywhere in the app to misuse.
 *   4. The one summary a descriptive axis does get — the modal stop — is allowed
 *      to come back as several stops. A tie is not resolved on the way out; see
 *      `modalStops`. A single number was never the only way to hide a
 *      disagreement, and a single *label* over a 1-vs-1 split hid one just as
 *      confidently.
 *
 * A split distribution is the interesting signal, not an error state: "we
 * disagreed hard about the luck in this" is the sentence the app exists to
 * produce. Hence `split` is surfaced rather than smoothed away.
 *
 * ## The two derived numbers, and how far each of them reaches
 *
 * Both live at the bottom of this file and both average, so it is worth being
 * exact about what each one averages.
 *
 * `topicValue` is six questions *about one axis*, for *one person*. It never
 * crosses from one aspect to another. Six answers about how the rules read are
 * six readings of one measurement.
 *
 * `overallValue` does cross, and that is the whole of it: the evaluative topics
 * one seat gave, meaned into "how good was the night". Six different axes are six
 * different measurements, and averaging them is a claim — the claim the product
 * now makes on purpose. It is bounded three ways. It is per seat, never pooled
 * across the table here. It skips DESCRIPTIVE topics, because "how much luck is
 * in this" is not a verdict to be folded into one. And it is never written down:
 * the row that exists is the override somebody typed instead of it.
 *
 * Both take the aspect's `kind` and both refuse a DESCRIPTIVE one for the same
 * reason — a follow-up under a descriptive axis is describing the same thing more
 * finely rather than judging it. A descriptive topic still shows a value the
 * rater set by hand; that is a rating, not an average, and it still does not
 * reach the overall.
 */

import { stopIndexForStar } from "./stars.ts";
import type { AspectKind, Stop } from "./types.ts";

/**
 * Range at which a group is treated as split rather than merely scattered.
 * Four half-stars is two whole stars — 40% of the scale, end to end — and every
 * question in the app is now on that one scale, so this means the same thing
 * wherever it is applied: two people watched different games.
 */
export const SPLIT_THRESHOLD = 4;

export interface Distribution {
  n: number;
  mean: number | null;
  median: number | null;
  min: number;
  max: number;
  spread: number;
  split: boolean;
  counts: Map<number, number>;
}

/**
 * An empty distribution reports `n: 0` with null centres. `min`/`max`/`spread`
 * are zeroed rather than made nullable because callers gate on `n` anyway, and
 * three more nullable numbers would spread null-checks through every renderer.
 */
function empty(): Distribution {
  return { n: 0, mean: null, median: null, min: 0, max: 0, spread: 0, split: false, counts: new Map() };
}

export function distribution(values: number[]): Distribution {
  if (values.length === 0) return empty();

  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const counts = new Map<number, number>();
  let sum = 0;
  for (const v of sorted) {
    sum += v;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }

  const min = sorted[0]!;
  const max = sorted[n - 1]!;
  const mid = n >> 1;
  const median = n % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
  const spread = max - min;

  return { n, mean: sum / n, median, min, max, spread, split: spread >= SPLIT_THRESHOLD, counts };
}

/** Mean of the participants' 1–10 "desire to play again" for one session. */
export function sessionHeadline(ratings: { value: number }[]): Distribution {
  return distribution(ratings.map((r) => r.value));
}

/**
 * Game headline: the mean of each completed session's headline mean.
 *
 * Sessions weigh equally, deliberately. Pooling every rating instead would let
 * a six-player night outvote a two-player night three to one, so a game's score
 * would track how many people happened to be in the room rather than how much
 * the group wants to play it again.
 *
 * Returns null when no session has a rating — "we have not rated this" is not
 * the same statement as "this scored zero".
 */
export function gameHeadline(sessions: Distribution[]): Distribution | null {
  const means: number[] = [];
  for (const s of sessions) {
    if (s.n === 0 || s.mean === null) continue;
    means.push(s.mean);
  }
  if (means.length === 0) return null;
  return distribution(means);
}

export interface RatingCell {
  value: number | null;
  na: 0 | 1;
}

export interface AspectProfile {
  distribution: Distribution;
  /**
   * The verdict line, already phrased for the screen. For DESCRIPTIVE aspects
   * this *is* the headline output: the mean of a bipolar Luck↔Strategy axis is
   * close to meaningless on its own — "0.2" describes no game anybody played,
   * while "mostly: Balanced, with two people saying Luck dominates" describes
   * one.
   *
   * When nothing holds the mode alone it names the tie instead of naming a
   * winner. Every consumer prints this string bare, as the group's answer, so
   * the honesty has to be inside the string — see `verdictLine`.
   */
  modalLabel: string | null;
  /**
   * Every stop that holds the top count, in track order. One entry is a real
   * mode; two or more is a tie, and the tie is a finding rather than a gap in
   * the data. Carried as well as phrased so a renderer can lay a tie out its own
   * way — one chip per tied stop, say — without re-deriving it from `counts`.
   */
  modalStops: Stop[];
  /** Explicit "not applicable" answers. Different information from a skip. */
  na: number;
}

/**
 * Every stop that holds the top count, in track order.
 *
 * A list and not a winner. This used to return a single stop and break ties
 * toward the centre of the track, on the theory that centre-ward is the
 * conservative direction — an exact tie between "Pure luck" and "Balanced" is
 * not evidence for the extreme. Conservative or not, it was still a guess, and
 * on the commonest shape of tie there is — two people, one each way — it printed
 * a group verdict that neither of them had given. Two testers hit it, and it is
 * the one kind of wrongness this app cannot afford: the room can see on each
 * other's faces that they disagreed, so a confident headline over a 1-vs-1 split
 * discredits every other number on the screen.
 *
 * So the tie leaves here intact and `verdictLine` says so out loud. Track order
 * keeps it deterministic — the same input always renders the same strip, which
 * was the other thing the centre-ward rule was buying.
 */
function modalStops(counts: Map<number, number>, stops: Stop[]): Stop[] {
  if (stops.length === 0 || counts.size === 0) return [];

  // Ratings are half-stars and stops are the rubric's words for them, so the
  // counts are bucketed onto the stop each rating lands nearest before the mode
  // is taken. Two people half a star apart are agreeing about the evening in the
  // only vocabulary this screen has, and counting them as two different answers
  // would manufacture a tie out of a rounding difference.
  const perStop = new Map<number, number>();
  for (const [value, count] of counts) {
    const index = stopIndexForStar(value, stops.length);
    perStop.set(index, (perStop.get(index) ?? 0) + count);
  }

  const tied: Stop[] = [];
  let top = 0;

  // Track order, which is the rubric's own order. A stop's numeric `value` no
  // longer orders anything — after the move to stars it is a leftover id.
  for (let i = 0; i < stops.length; i++) {
    const count = perStop.get(i) ?? 0;
    if (count === 0) continue;
    if (count > top) {
      top = count;
      tied.length = 0;
    }
    if (count === top) tied.push(stops[i]!);
  }
  return tied;
}

/**
 * The tied stops as one line of copy.
 *
 * A single stop reads as itself. Several are spelled out in track order and
 * marked as a tie, because the three places that render this print it with no
 * framing of their own: a bare "Luck dominates / Skill dominates" reads as one
 * compound verdict — some third thing the group settled on — rather than as two
 * people who did not agree. Naming it is what makes the split legible without
 * every renderer having to learn about ties first.
 *
 * "Tied" and deliberately not "Split". `Distribution.split` is a different fact
 * — four steps or more from end to end — and already has its own badge and its
 * own sentence on screen. A two-player table can be tied on adjacent stops
 * without being far apart, and a five-player table can be split wide open with
 * one clear mode; reusing the word would blur two findings into one.
 */
function verdictLine(tied: Stop[]): string | null {
  if (tied.length === 0) return null;
  if (tied.length === 1) return tied[0]!.label;
  return `Tied: ${tied.map((s) => s.label).join(" / ")}`;
}

/**
 * One aspect across the participants of one session (or across sessions, if the
 * caller has already pooled comparable rubric versions).
 *
 * `na` rows are dropped from the distribution and counted separately: someone
 * saying "this axis does not apply to this game" must not drag a mean, but it
 * is a real answer and worth showing beside the `n`.
 *
 * `kind` is required rather than optional because it decides whether a centre
 * exists at all. For a DESCRIPTIVE axis `mean` and `median` come back null —
 * the plan's rule that descriptive axes are never averaged is enforced here, at
 * the only place that could compute the average, instead of being left as a
 * convention every renderer has to remember. Everything a descriptive axis
 * actually displays — the counts, the min/max, the spread, the split flag and
 * the modal label — is untouched.
 */
export function aspectProfile(ratings: RatingCell[], stops: Stop[], kind: AspectKind): AspectProfile {
  const values: number[] = [];
  let na = 0;
  for (const r of ratings) {
    if (r.na === 1) {
      na++;
      continue;
    }
    if (r.value === null) continue; // skipped; aspects are optional by design
    values.push(r.value);
  }

  const dist = distribution(values);
  const tied = modalStops(dist.counts, stops);
  const shown = kind === "DESCRIPTIVE" ? { ...dist, mean: null, median: null } : dist;
  return { distribution: shown, modalLabel: verdictLine(tied), modalStops: tied, na };
}

export interface AspectSlice {
  id: string;
  name: string;
  kind: AspectKind;
  stops: Stop[];
  ratings: RatingCell[];
}

export interface Disagreement {
  id: string;
  name: string;
  kind: AspectKind;
  profile: AspectProfile;
}

/**
 * Ranks a session's aspects by how far apart the group landed, widest first —
 * the input to "you split hardest on Interaction".
 *
 * Aspects nobody rated are dropped rather than ranked last: an absent answer is
 * silence, and silence is not agreement. Ties fall back to `n` (a wide spread
 * across five raters is a stronger claim than the same spread across two) and
 * then to the name, so the ordering is stable between renders.
 */
export function disagreement(aspects: AspectSlice[]): Disagreement[] {
  const rows: Disagreement[] = [];
  for (const a of aspects) {
    const profile = aspectProfile(a.ratings, a.stops, a.kind);
    if (profile.distribution.n === 0) continue;
    rows.push({ id: a.id, name: a.name, kind: a.kind, profile });
  }

  return rows.sort((x, y) => {
    const bySpread = y.profile.distribution.spread - x.profile.distribution.spread;
    if (bySpread !== 0) return bySpread;
    const byN = y.profile.distribution.n - x.profile.distribution.n;
    if (byN !== 0) return byN;
    return x.name < y.name ? -1 : x.name > y.name ? 1 : 0;
  });
}

// ---------------------------------------------------------------------------
// A topic's value for one person
// ---------------------------------------------------------------------------

/**
 * One person's number for one topic, and where it came from.
 *
 * `source` is not decoration. The two numbers behave differently under a
 * correction — a set one stays put until its owner moves it, a derived one moves
 * the moment they change a follow-up — so a screen that shows the number without
 * saying which kind it is has told half the story, and the half it dropped is the
 * one that explains why the figure changed while nobody touched it.
 *
 * `answered` and `of` travel with the value for the same reason. A three is a
 * different claim depending on whether three people-worth of questions or one
 * fed it, and the provenance line ("worked out from 3 of 6 answers") is the only
 * place a reader can see that the other three were skipped rather than answered
 * badly.
 */
export interface TopicValue {
  /**
   * The rating, 1..10 half-stars, or null when this person has said nothing.
   *
   * A derived value is deliberately **not** rounded onto the half-star ladder.
   * Every stored rating is an integer here because a stored 3.47 would be a
   * number nobody chose; a derived one is not stored and not chosen either way,
   * and rounding it would claim a precision the mean does not have while hiding
   * the difference between "everyone said four" and "two said three, two said
   * five". The star row draws a fraction correctly and `formatStars` prints one
   * decimal, so the exactness costs nothing on screen.
   */
  value: number | null;
  /** `set` means the rater put this number on the topic themselves. */
  source: "set" | "derived";
  /**
   * Follow-ups that fed the mean. Zero whenever `source` is `set`.
   *
   * There is deliberately no companion "of how many". This function is handed
   * the cells one device can read, which on a blind night is fewer than the
   * night asked — so any total it computed would be the redaction's number, not
   * the night's. The caller has the question list and can count it itself.
   */
  answered: number;
  /** They said the whole topic does not apply. */
  na: boolean;
}

/**
 * A topic's value for one rater: what they set, or what their follow-ups say.
 *
 * The order of the three branches is the product decision, so it is written out
 * rather than left to fall out of the code:
 *
 *  1. **A number the rater put on the topic wins.** They were shown the worked-out
 *     figure, they armed the control and they moved it — that is not a rating
 *     given in ignorance of the follow-ups, it is a rating given *instead* of
 *     them, and treating it as stale would make the override gesture do nothing.
 *  2. **Otherwise the answered follow-ups make it.** Skips and "doesn't apply"
 *     drop out of the mean entirely, which is the whole of the requirement that a
 *     question nobody answered must not pull a topic down. Three answers out of
 *     six give a mean of three, not a mean of six with three zeroes in it.
 *  3. **An n/a on the topic loses to a follow-up.** Somebody who said "this does
 *     not apply" and then went and answered three of its questions has changed
 *     their mind in the only way the data can express, and the newer fact is the
 *     answers. With no answers at all the n/a stands, and stands as an answer —
 *     the reveal gate counts it, so it must not read as silence here either.
 *
 * `kind` is asked for the same reason `aspectProfile` asks: a DESCRIPTIVE axis
 * has no average, so there is no branch 2 for one. See the note at the top of
 * this file.
 */
export function topicValue(
  kind: AspectKind,
  own: RatingCell | null,
  followUps: readonly RatingCell[],
): TopicValue {
  const set = (value: number | null, na: boolean): TopicValue => ({
    value,
    source: "set",
    answered: 0,
    na,
  });

  if (own && own.na !== 1 && typeof own.value === "number") return set(own.value, false);

  if (kind !== "DESCRIPTIVE") {
    let sum = 0;
    let answered = 0;
    for (const cell of followUps) {
      if (cell.na === 1 || typeof cell.value !== "number") continue;
      sum += cell.value;
      answered++;
    }
    if (answered > 0) {
      return { value: sum / answered, source: "derived", answered, na: false };
    }
  }

  if (own && own.na === 1) return set(null, true);
  return { value: null, source: "derived", answered: 0, na: false };
}

// ---------------------------------------------------------------------------
// The night's overall, for one person
// ---------------------------------------------------------------------------

/** A topic as the overall sees it: its kind, and this seat's value for it. */
export interface TopicContribution {
  kind: AspectKind;
  value: TopicValue;
}

export interface OverallValue {
  /**
   * 1..10 half-stars, or null when this seat has rated nothing to derive from
   * and typed no override.
   *
   * Unrounded when derived, for the reason `TopicValue.value` is: it is not
   * stored and nobody chose it, so rounding would claim a precision the mean
   * does not have. `formatStars` prints one decimal.
   */
  value: number | null;
  /** `set` means this seat armed the stars and typed a number over the mean. */
  source: "set" | "derived";
  /**
   * Topics that fed the mean. Zero whenever `source` is `set`.
   *
   * No companion total, for the same reason `TopicValue` has none: this is
   * handed the topics one device can read, which on a blind night is fewer than
   * the night asked. The caller has the topic list.
   */
  topics: number;
}

/**
 * One person's overall for one night: what they typed, or what their topics say.
 *
 * Two branches, in this order, and the order is the product decision:
 *
 *  1. **An override wins.** They were shown the worked-out figure beside the
 *     headline, they armed the control and they moved it. Letting the mean win
 *     back would make the override gesture do nothing the next time a topic
 *     moved.
 *  2. **Otherwise the topics make it.** A topic with no answer drops out
 *     entirely rather than counting as a zero — the same rule that governs a
 *     skipped follow-up inside `topicValue`, and the whole of "skipped questions
 *     are not used for calculating the rating". So does an n/a topic: "this does
 *     not apply to this game" is an answer about the axis, not a verdict on the
 *     night, and averaging it in at any value would be inventing one.
 *
 * DESCRIPTIVE topics never reach the mean, however they got their value. See the
 * note at the top of this file.
 */
export function overallValue(
  override: number | null,
  topics: readonly TopicContribution[],
): OverallValue {
  if (override !== null) return { value: override, source: "set", topics: 0 };

  let sum = 0;
  let counted = 0;
  for (const topic of topics) {
    if (topic.kind === "DESCRIPTIVE") continue;
    if (topic.value.na || topic.value.value === null) continue;
    sum += topic.value.value;
    counted++;
  }
  if (counted === 0) return { value: null, source: "derived", topics: 0 };
  return { value: sum / counted, source: "derived", topics: counted };
}
